import React from 'react'
import { useApp } from '../context/AppContext'
import { Icon, useT } from '../shared'
import { useOrgName } from "../shared/BrandLogo"
import { Btn, LevelBadge } from '../shared/components'
import { tierShort } from '../lib/tier'
import { tintFg } from '../lib/tint'

/* ============================================================
   Program Selector Screen — FR-2.4
   Shown to multi-program participants right after login so they
   can pick which OPRC program to enter. Admin/SUPER_ADMIN skip
   this screen entirely (handled in AppContext).
   ============================================================ */

export default function ProgramSelectorScreen() {
  const { programs, setCurrentProgramId, navigate, user, logout, lang } = useApp()
  const orgName = useOrgName()
  const t = useT()

  const handlePick = (programId) => {
    setCurrentProgramId(programId)
    navigate('dashboard')
  }

  if (!programs || programs.length === 0) {
    return (
      <div className="program-selector empty">
        <div className="program-selector-inner" style={{ textAlign: 'center', maxWidth: 480 }}>
          <div style={{
            width: 64, height: 64, borderRadius: '50%',
            background: 'var(--surface-2)', display: 'grid', placeItems: 'center',
            margin: '0 auto 20px',
          }}>
            <Icon name="warning" size={28} color="var(--text-tertiary)" />
          </div>
          <h1 className="h1">
            {lang === 'id' ? 'Belum ada program' : 'No program assigned'}
          </h1>
          <p className="muted mt-2">
            {lang === 'id'
              ? 'Anda belum terdaftar di program mana pun. Hubungi admin untuk enrollment.'
              : 'You are not enrolled in any program yet. Please contact your administrator.'}
          </p>
          {/* Peserta yang programnya sudah ditutup (nonaktif) tidak punya program lagi,
              tetapi sertifikatnya tetap miliknya — dan layar ini tanpa sidebar, jadi
              tanpa tombol ini tak ada jalan ke sana. */}
          {user?.role === 'PARTICIPANT' && (
            <Btn variant="secondary" icon={<Icon name="cert" size={14} />} onClick={() => navigate('certificates')} style={{ marginTop: 24 }}>
              {lang === 'id' ? 'Sertifikat saya' : 'My certificates'}
            </Btn>
          )}
          <Btn variant="ghost" icon={<Icon name="logout" size={14} />} onClick={logout} style={{ marginTop: user?.role === 'PARTICIPANT' ? 8 : 24 }}>
            {t.logout}
          </Btn>
        </div>
      </div>
    )
  }

  return (
    <div className="program-selector">
      <div className="program-selector-inner">
        <div className="row between" style={{ alignItems: 'flex-start', marginBottom: 32 }}>
          <div>
            <div className="eyebrow" style={{ marginBottom: 6 }}>{orgName}</div>
            <h1 className="h1">
              {lang === 'id' ? 'Pilih Program' : 'Choose your program'}
            </h1>
            <p className="muted mt-2">
              {lang === 'id'
                ? `Halo ${user?.fullName || ''}, Anda terdaftar di beberapa program. Pilih satu untuk melanjutkan.`
                : `Hello ${user?.fullName || ''}, you are enrolled in multiple programs. Pick one to continue.`}
            </p>
          </div>
          <Btn variant="ghost" size="sm" icon={<Icon name="logout" size={14} />} onClick={logout}>
            {t.logout}
          </Btn>
        </div>

        <div className="program-grid">
          {programs.map((p) => (
            <button
              key={p.id}
              className="program-card"
              onClick={() => handlePick(p.id)}
              style={{
                borderTop: `4px solid ${p.color_theme || 'var(--program-color)'}`,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 16 }}>
                <div
                  className="program-card-icon"
                  style={{
                    background: (p.color_theme || 'var(--program-color)') + '22',
                    color: tintFg(p.color_theme || 'var(--program-color)', 70),
                  }}
                >
                  {p.icon ? (
                    <Icon name={p.icon} size={28} color={p.color_theme || 'currentColor'} />
                  ) : (
                    <span className="mono" style={{ fontSize: 18 }}>
                      {tierShort(p) || p.code?.slice(0, 2).toUpperCase()}
                    </span>
                  )}
                </div>
                {p.level && (
                  <LevelBadge level={p.level} programColor={p.color_theme} />
                )}
              </div>
              <div className="program-card-body">
                <div className="program-card-code">{p.code?.toUpperCase()}</div>
                <div className="program-card-title">{p.name}</div>
                {p.description && <p className="program-card-desc">{p.description}</p>}
              </div>
              <div className="program-card-cta">
                <span>{lang === 'id' ? 'Masuk' : 'Enter'}</span>
                <Icon name="chevron_right" size={14} />
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}
